import React, { useEffect, useState } from 'react'
import { useTranslation } from '../i18n/LanguageContext'
import { browserPlanStorage } from '../lib/planDrafts.js'
import { KENTEKEN_PREFIX, KENTEKEN_EVENT, listKentekenDrafts, readKentekenDraft,
  writeKentekenDraft, removeKentekenDraft, validDraftMileage } from '../lib/kentekenDrafts.js'
import './PlanDrafts.css'

export const notifyKentekenChanged = (draft, previousRevision) => window.dispatchEvent(new CustomEvent(KENTEKEN_EVENT, { detail: { draft, previousRevision } }))
export const KENTEKEN_ERRORS = {
  unavailable: 'Browser storage is unavailable or full. Your plate and mileage have not been saved.',
  invalid: 'A saved plate check could not be read. It has not been overwritten or removed.',
  conflict: 'This plate draft changed in another tab. Your inputs have not overwritten it. Reopen the latest saved draft before updating.',
  input: 'Enter a valid plate and a whole-number odometer, or leave mileage blank.',
  limit: 'You have 20 saved plate checks. Remove one before saving another.',
}
const ERRORS = KENTEKEN_ERRORS

// Same-tab plate and scenario controls share acknowledged revisions. A storage
// event from another tab must NOT advance this token and hide a write conflict.
export function useKentekenDraftRecord(plate, resumedDraft) {
  const [initial] = useState(() => readKentekenDraft(browserPlanStorage(), plate))
  const [saved, setSaved] = useState(resumedDraft?.plate === plate ? resumedDraft : initial.draft)
  const [error, setError] = useState(initial.error)
  useEffect(() => {
    const update = event => {
      const { draft, previousRevision } = event.detail || {}
      if (draft?.plate === plate) setSaved(current => (current?.revision ?? null) === previousRevision ? draft : current)
    }
    window.addEventListener(KENTEKEN_EVENT, update)
    return () => window.removeEventListener(KENTEKEN_EVENT, update)
  }, [plate])
  const accept = result => {
    setError(result.error)
    if (result.draft) {
      const previousRevision = saved?.revision ?? null
      setSaved(result.draft); notifyKentekenChanged(result.draft, previousRevision)
    }
  }
  return { saved, error, accept }
}

export function downloadPlateScenario(draft, payload = draft?.specialist?.payload) {
  const data = { schema: 'automargin-plate-scenario-export.v1', exported_at: new Date().toISOString(),
    draft, current_inputs: payload, limitation: 'Personal assumptions, not a valuation, source evidence, current quotes or buying approval.' }
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }))
  const link = document.createElement('a'); link.href = url
  link.download = `automargin-plate-scenario-${draft?.plate || 'unsaved'}.json`
  link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000)
}
export function useKentekenDrafts() {
  const [state, setState] = useState(() => listKentekenDrafts(browserPlanStorage()))
  useEffect(() => {
    const refresh = event => { if (event.type !== 'storage' || event.key === null || event.key?.startsWith(KENTEKEN_PREFIX)) setState(listKentekenDrafts(browserPlanStorage())) }
    window.addEventListener(KENTEKEN_EVENT, refresh); window.addEventListener('storage', refresh)
    return () => { window.removeEventListener(KENTEKEN_EVENT, refresh); window.removeEventListener('storage', refresh) }
  }, [])
  return state
}

export function KentekenDraftSave({ plate, mileage, resumedDraft }) {
  const { t } = useTranslation()
  const { saved, error, accept } = useKentekenDraftRecord(plate, resumedDraft)
  const dirty = !saved || saved.mileage !== mileage
  function save() {
    const result = writeKentekenDraft(browserPlanStorage(), { plate, mileage, expectedRevision: saved?.revision ?? null })
    accept(result)
  }
  return <div className="plan-draft-controls kenteken-draft-save">
    <div className="plan-draft-actions"><button type="button" disabled={!dirty || !validDraftMileage(mileage)} onClick={save}>
      {t(saved ? 'Update saved plate inputs' : 'Save plate and mileage on this device')}</button></div>
    <small role="status">{t(saved && !dirty ? 'Plate inputs saved on this browser. Resume under Saved; the value is not saved.'
      : 'Optional local draft only. Other people using this browser can read the plate and mileage. No account sync; clearing browser data removes it.')}</small>
    {error && <p role="alert">{t(ERRORS[error])}</p>}
  </div>
}

export default function SavedKentekenDrafts({ onResume }) {
  const { t } = useTranslation(), { drafts, error } = useKentekenDrafts()
  const [confirm, setConfirm] = useState(null), [problem, setProblem] = useState(null)
  function remove(draft) {
    const failure = removeKentekenDraft(browserPlanStorage(), draft)
    setProblem(failure)
    if (!failure) { setConfirm(null); window.dispatchEvent(new Event(KENTEKEN_EVENT)) }
  }
  return <section className="saved-plans saved-kenteken" aria-label={t('Saved plate inputs')}>
    <h2>{t('Saved plate inputs')}</h2>
    <p>{t('Reopen your plate and mileage, then check RDW and calculate again. No old valuation is restored.')}</p>
    {!drafts.length && !error && <p>{t('No plate drafts yet. Look up a plate and choose Save plate and mileage on this device.')}</p>}
    {(error || problem) && <p role="alert">{t(ERRORS[problem || error])}</p>}
    <div className="saved-plan-grid">{drafts.map(draft => <article className="saved-plan-card" key={draft.plate}>
      <h3>{draft.plate}</h3><p>{draft.mileage === '' ? t('Mileage not entered') : `${Number(draft.mileage).toLocaleString('nl-NL')} km`}</p>
      <small>{t('Saved')}: {new Date(draft.saved_at).toLocaleString('nl-NL')}</small>
      {draft.specialist && <p>{t('Specialist assumptions saved for {km} km. Fresh lookup and quote recheck required.', { km: Number(draft.specialist.mileage).toLocaleString('nl-NL') })}</p>}
      <div className="plan-draft-actions"><button type="button" onClick={() => onResume(draft)}>{t('Reopen plate inputs')}</button>
        {draft.specialist && <button type="button" className="plan-secondary" onClick={() => downloadPlateScenario(draft)}>{t('Download saved plate scenario')}</button>}
        <button type="button" className="plan-secondary" onClick={() => setConfirm(draft.plate)}>{t('Remove plate draft')}</button></div>
      {confirm === draft.plate && <div role="group" aria-label={t('Confirm plate draft removal')}>
        <p>{t('Remove only this plate draft from this browser? This cannot be undone.')}</p>
        {draft.specialist && <p>{t('This also removes its saved specialist assumptions. Download them first to keep a copy.')}</p>}
        <button type="button" onClick={() => remove(draft)}>{t('Remove this plate draft')}</button>{' '}
        <button type="button" className="plan-secondary" onClick={() => setConfirm(null)}>{t('Cancel')}</button>
      </div>}
    </article>)}</div>
  </section>
}
